// Pre-payout segment building. Maps `month.events.ineligibilityReason`
// (calc-layer classification) to display labels and groups consecutive months
// sharing the same reason key + claimant (waiting-period runs also split on
// care environment) so the UI can collapse them into a summary view. Grouping
// is NOT bounded by policy year — a run that keeps the same reason/claimant
// (e.g. multi-year pre-policy care) collapses into one segment spanning years;
// `segment.policyYear` is then just the first month's year.

import { getCumulativeIneligibleCostSeries } from '@shared/calculations/funding/projection/projectionTotals';

// Reason key for the UI. 'other' is the catch-all when calc reports null
// (shouldn't appear in the pre-payout slice by construction, but kept for
// exhaustiveness).
export type ReasonKey = ProjectionIneligibilityReason | 'other';

export interface ReasonLabel {
  key: ReasonKey;
  text: string;
  tone: string;
}

// `il-not-covered` is intentionally absent: the claim-projection engine covers
// Independent Living care as home care (room & board is booked as a separate
// out-of-pocket dollar line, not a non-paying-day reason), so the engine never
// emits that reason. The key is retained in the shared type for legacy
// compatibility; the map is Partial so this display layer needn't carry it.
export const REASON_TOOLTIPS: Partial<Record<ReasonKey, string | null>> = {
  'lt-2-adl':
    'The policy only pays once the client needs help with at least 2 of the 6 standard activities of daily living (bathing, dressing, eating, toileting, transferring, continence). During the early-care phase with fewer than 2 ADLs, the policy treats these months as ineligible — even though care costs are accruing.',
  'in-wp':
    'The elimination (waiting) period is the number of qualifying days the client must receive care before the policy starts paying. Days during this window count toward satisfying it but receive no benefit. Once the period is fully served, ongoing benefits begin.',
  'pre-policy':
    'This care occurs before the policy’s premium-start year, so the policy does not yet exist and cannot pay — the full cost is out of pocket regardless of the care details. The <2 ADL window and elimination (waiting) period only affect what the policy pays once it’s in force, so these months aren’t split by those reasons. Once the policy takes effect, ongoing benefits begin.',
  other: null,
};

export function reasonForMonth(month: MonthlyEntry): ReasonLabel {
  switch (month.events.ineligibilityReason) {
    case 'lt-2-adl':
      return { key: 'lt-2-adl', text: 'Less than 2 ADLs active', tone: 'text-gray-900' };
    case 'in-wp':
      return { key: 'in-wp', text: 'Within waiting period', tone: 'text-gray-900' };
    case 'pre-policy':
      return { key: 'pre-policy', text: 'Before policy start', tone: 'text-gray-900' };
    default:
      return { key: 'other', text: '—', tone: 'text-gray-400' };
  }
}

// A group of consecutive months sharing the same reason key + claimant (and,
// for waiting-period runs, the same care environment). Used to collapse
// repetitive runs ("Months 1–11: <2 ADL") into a single summary row. Not bounded
// by policy year — see the file header.
export interface PreSegment {
  id: string;
  // First month's policy year. The segment is NOT split on policy year, so a
  // run can span years (e.g. multi-year pre-policy care); this is the start year.
  policyYear: number;
  reason: ReasonLabel;
  months: MonthlyEntry[];
  startMonthIndex: number;
  endMonthIndex: number;
  // Open state going *into* the first month of the segment (i.e. the close
  // state of the prior month, or `initialState` for the first segment). Kept
  // for downstream consumers that need pre-segment pool/WP balances; do NOT
  // read `waitingPeriod` here for "WP total" display — it reflects whatever
  // the calc has already consumed during a same-row gate→WP transition, not
  // the policy-configured total. Use `breakdown` for day counts instead.
  openStateAtStart: MonthlyState;
  // Cumulative out-of-pocket cost at the segment's end / start. Both are
  // computed against the full projection so a downstream EP retroactive
  // lump can correctly drop these values within the same projection view.
  endCumulativeIneligible: number;
  startCumulativeIneligible: number;
  // Min/max projected cost within the segment (usually equal — cost is
  // flat within a policy year per the calc's annual stepping).
  minCost: number;
  maxCost: number;
  hasMilestones: boolean;
  // Sum of per-month `ineligibleDayBreakdown` across this segment. Drives
  // the stacked-reason display: a segment whose primary reason is
  // `lt-2-adl` can still carry inWpDays > 0 from the transition month
  // where the 2-ADL gate ended and WP started serving inside the same row.
  breakdown: IneligibleDayBreakdown;
}

function emptyBreakdown(): IneligibleDayBreakdown {
  return { lt2AdlDays: 0, inWpDays: 0, ilNotCoveredDays: 0, prePolicyDays: 0 };
}

function addBreakdown(
  acc: IneligibleDayBreakdown,
  add: IneligibleDayBreakdown,
): IneligibleDayBreakdown {
  return {
    lt2AdlDays: acc.lt2AdlDays + add.lt2AdlDays,
    inWpDays: acc.inWpDays + add.inWpDays,
    ilNotCoveredDays: acc.ilNotCoveredDays + add.ilNotCoveredDays,
    prePolicyDays: acc.prePolicyDays + add.prePolicyDays,
  };
}

// Translate a breakdown into a stacked display list. Each entry is a reason
// label + day count; order matches the calc's gate-resolution priority so
// the stacked list reads chronologically within a row (gate → WP → IL).
export interface BreakdownLine {
  reason: ReasonLabel;
  days: number;
}

export function breakdownLines(breakdown: IneligibleDayBreakdown): BreakdownLine[] {
  const lines: BreakdownLine[] = [];
  if (breakdown.lt2AdlDays > 0) {
    lines.push({
      reason: { key: 'lt-2-adl', text: 'Less than 2 ADLs active', tone: 'text-gray-900' },
      days: breakdown.lt2AdlDays,
    });
  }
  if (breakdown.inWpDays > 0) {
    lines.push({
      reason: { key: 'in-wp', text: 'Within waiting period', tone: 'text-gray-900' },
      days: breakdown.inWpDays,
    });
  }
  if (breakdown.prePolicyDays > 0) {
    lines.push({
      reason: { key: 'pre-policy', text: 'Before policy start', tone: 'text-gray-900' },
      days: breakdown.prePolicyDays,
    });
  }
  return lines;
}

export function buildPreSegments(
  months: MonthlyEntry[],
  initialState: MonthlyState,
): PreSegment[] {
  const segments: PreSegment[] = [];
  const cumIneligibleSeries = getCumulativeIneligibleCostSeries(months);
  months.forEach((month, i) => {
    const openState = i === 0 ? initialState : months[i - 1].state;
    const reason = reasonForMonth(month);
    const prev = segments[segments.length - 1];
    // Group by reason key. Keep waiting-period segments split by care
    // environment so the summary row's WP totals stay bucket-accurate
    // (home vs facility can have different configured WP days). On joint
    // policies the pre-payout slice interleaves both spouses, who serve their
    // own EP independently — split by claimant so a segment never merges two
    // spouses' runs (which would mix their cost + WP-day math). Single-life
    // rows have claimant undefined on both sides, so this is a no-op there.
    const prevMonth = prev?.months[prev.months.length - 1];
    const sameSegment =
      prev &&
      prev.reason.key === reason.key &&
      prevMonth?.context.claimant === month.context.claimant &&
      (reason.key !== 'in-wp' ||
        prevMonth?.context.careEnvironment === month.context.careEnvironment);

    if (sameSegment) {
      prev.months.push(month);
      prev.endMonthIndex = month.context.monthIndex;
      prev.endCumulativeIneligible = cumIneligibleSeries[i];
      prev.minCost = Math.min(prev.minCost, month.facts.effectiveCareCost);
      prev.maxCost = Math.max(prev.maxCost, month.facts.effectiveCareCost);
      prev.hasMilestones = prev.hasMilestones || month.events.milestones.length > 0;
      prev.breakdown = addBreakdown(prev.breakdown, month.events.ineligibleDayBreakdown);
    } else {
      segments.push({
        id: `${month.context.policyYear}-${reason.key}-${i}`,
        policyYear: month.context.policyYear,
        reason,
        months: [month],
        startMonthIndex: month.context.monthIndex,
        endMonthIndex: month.context.monthIndex,
        openStateAtStart: openState,
        startCumulativeIneligible: cumIneligibleSeries[i],
        endCumulativeIneligible: cumIneligibleSeries[i],
        minCost: month.facts.effectiveCareCost,
        maxCost: month.facts.effectiveCareCost,
        hasMilestones: month.events.milestones.length > 0,
        breakdown: addBreakdown(emptyBreakdown(), month.events.ineligibleDayBreakdown),
      });
    }
  });
  return segments;
}
